import { forwardRef, useImperativeHandle, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { useCheckoutContext } from '../../context/CheckoutContextCoCo.js';
import { useSubscriptionConfigFormCoCo } from '../../hooks/useSubscriptionConfigFormCoCo.js';
import { isSimSectionHidden } from '../../utils/simCardSectionUtilsCoCo.js';
import { SimCardSectionCoCo } from '../shared/SimCardSectionCoCo.js';
import { FixedBroadbandProductConfigCoCo } from './FixedBroadbandProductConfigCoCo.js';
import type { ConfigHandle, ProductConfigProps } from '../productConfigTypesCoCo.js';

type SimConfigProps = Pick<ProductConfigProps, 'basketItem' | 'orderItem' | 'phoneNumbers' | 'schema'>;

const FixedMobileBroadbandSimConfigCoCo = forwardRef<ConfigHandle, SimConfigProps>(
  ({ basketItem, orderItem, phoneNumbers, schema }, ref) => {
    const { mdmId, selectedProductOffers } = useCheckoutContext();
    const cartItemInstanceId = `${basketItem.id}-${orderItem.basketItemIndex}`;
    const { innerFormMethods, formState, errors, validateAllFields, scrollToFirstError } =
      useSubscriptionConfigFormCoCo({
        cartItemInstanceId,
        basketItem,
        mdmId,
        phoneNumbers,
        selectedProductOffers,
      });

    useImperativeHandle(
      ref,
      () => ({
        validate: validateAllFields,
        scrollToFirstError,
        getErrorCount: () => Object.keys(errors).length,
      }),
      [errors, scrollToFirstError, validateAllFields]
    );

    return (
      <FormProvider {...innerFormMethods}>
        <div className="of-order-item-unit-coco__config-box" hidden={isSimSectionHidden(schema, basketItem)}>
          <SimCardSectionCoCo
            schema={schema}
            basketItem={basketItem}
            cartItemInstanceId={cartItemInstanceId}
            formState={formState}
            errors={errors}
          />
        </div>
      </FormProvider>
    );
  }
);

FixedMobileBroadbandSimConfigCoCo.displayName = 'FixedMobileBroadbandSimConfigCoCo';

/** Fixed Mobile Broadband is configured like other fixed broadband, plus the choice of a new or existing SIM. */
export const FixedMobileBroadbandProductConfigCoCo = forwardRef<ConfigHandle, ProductConfigProps>((props, ref) => {
  const fixedBroadbandRef = useRef<ConfigHandle>(null);
  const simConfigRef = useRef<ConfigHandle>(null);
  const { schema, basketItem, orderItem, phoneNumbers } = props;

  useImperativeHandle(
    ref,
    () => ({
      validate: async () => {
        const results = await Promise.all([fixedBroadbandRef.current?.validate(), simConfigRef.current?.validate()]);
        return results.every(result => result ?? true);
      },
      scrollToFirstError: () => {
        if (fixedBroadbandRef.current?.getErrorCount()) {
          fixedBroadbandRef.current.scrollToFirstError();
        } else {
          simConfigRef.current?.scrollToFirstError();
        }
      },
      getErrorCount: () =>
        (fixedBroadbandRef.current?.getErrorCount() ?? 0) + (simConfigRef.current?.getErrorCount() ?? 0),
    }),
    []
  );

  return (
    <>
      <FixedBroadbandProductConfigCoCo ref={fixedBroadbandRef} {...props} />
      {(schema.needsPsim || schema.needsEsim || schema.needsActiveSim) && (
        <FixedMobileBroadbandSimConfigCoCo
          ref={simConfigRef}
          basketItem={basketItem}
          orderItem={orderItem}
          phoneNumbers={phoneNumbers}
          schema={schema}
        />
      )}
    </>
  );
});

FixedMobileBroadbandProductConfigCoCo.displayName = 'FixedMobileBroadbandProductConfigCoCo';
